import { z } from 'zod';
export const PointLedgerStatus = z.enum([
    'pending',
    'posted',
    'held',
    'rejected',
    'reversed',
    'capped',
]);
export const PointPool = z.object({
    id: z.string().uuid(),
    name: z.string(),
    label: z.string(),
    status: z.enum(['active', 'archived']),
    ownerGroupId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const PointPoolGroupLink = z.object({
    poolId: z.string().uuid(),
    groupId: z.string().uuid(),
    status: z.enum(['pending', 'active', 'revoked']),
    requestedByUserId: z.string().uuid().nullable(),
    requestedAt: z.string().datetime({ offset: true }).nullable(),
    consentedByUserId: z.string().uuid().nullable(),
    consentedAt: z.string().datetime({ offset: true }).nullable(),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
});
export const EngagementLeaderboardMetric = z.enum(['balance', 'lifetimeEarned']);
export const EngagementLeaderboardVisibility = z.enum(['members', 'managers', 'hidden']);
export const EngagementLeaderboardSettings = z.object({
    enabled: z.boolean(),
    visibility: EngagementLeaderboardVisibility,
    metric: EngagementLeaderboardMetric,
    limit: z.number().int().min(1).max(100),
    showExactPoints: z.boolean(),
    emptyMessage: z.string().max(200).nullable(),
});
export const EngagementMemberDisplaySettings = z.object({
    showTasks: z.boolean(),
    showRewards: z.boolean(),
    showPersonalLedger: z.boolean(),
    showHeldPoints: z.boolean(),
});
export const EngagementTaskDefaults = z.object({
    defaultDailyCap: z.number().int().min(0).max(1_000_000).nullable(),
    defaultDailyOverallCap: z.number().int().min(0).max(1_000_000).nullable().optional(),
    defaultReviewRequired: z.boolean(),
    defaultMinReviewPoints: z.number().int().min(0).max(100_000),
    dailyCapTimezone: z.string().min(1).max(80),
    memberVisibleStatuses: z.array(z.enum(['active', 'paused'])).min(1).max(2),
});
export const EngagementRiskPolicy = z.object({
    autoHoldHighValue: z.boolean(),
    highValueThreshold: z.number().int().min(0).max(100_000),
    requireReviewForManualAdjustments: z.boolean(),
    showReviewerNotesToMembers: z.boolean(),
});
export const EngagementRewardPolicy = z.object({
    enabled: z.boolean(),
    requiresApprovalDefault: z.boolean(),
    showClosedRewards: z.boolean(),
    defaultPerUserLimit: z.number().int().min(1).max(1_000_000).nullable(),
});
export const EngagementSettings = z.object({
    leaderboard: EngagementLeaderboardSettings,
    memberDisplay: EngagementMemberDisplaySettings,
    taskDefaults: EngagementTaskDefaults,
    riskPolicy: EngagementRiskPolicy,
    rewardPolicy: EngagementRewardPolicy,
});
export const PointAccount = z.object({
    poolId: z.string().uuid(),
    userId: z.string().uuid(),
    balance: z.number().int(),
    held: z.number().int().nonnegative().nullable(),
    lifetimeEarned: z.number().int().nonnegative(),
    lifetimeSpent: z.number().int().nonnegative(),
});
export const PointLedgerSource = z.object({
    kind: z.string(),
    label: z.string(),
    title: z.string().nullable(),
    url: z.string().nullable(),
    resourceKind: z.string().nullable(),
    resourceId: z.string().uuid().nullable(),
    taskId: z.string().uuid().nullable(),
    taskTitle: z.string().nullable(),
});
export const PointLedgerAdjustmentBatch = z.object({
    id: z.string().uuid(),
    size: z.number().int().positive(),
    index: z.number().int().positive(),
});
export const PointLedgerEntry = z.object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    userId: z.string().uuid(),
    amount: z.number().int(),
    status: PointLedgerStatus,
    category: z.string(),
    sourceKind: z.string(),
    reason: z.string(),
    source: PointLedgerSource.nullable().optional(),
    adjustmentBatch: PointLedgerAdjustmentBatch.nullable().optional(),
    userDisplayName: z.string().nullable().optional(),
    activityId: z.string().uuid().nullable(),
    ruleId: z.string().uuid().nullable(),
    reversalOfId: z.string().uuid().nullable(),
    reviewerNote: z.string().nullable().optional(),
    createdAt: z.string().datetime({ offset: true }),
    postedAt: z.string().datetime({ offset: true }).nullable(),
});
export const TaskBeneficiary = z.enum([
    'actor',
    'subject',
    'payload.userId',
    'payload.authorId',
    'payload.respondentId',
]);
export const ArticleReadTaskScope = z.enum([
    'anyArticle',
    'currentGroupDistributedArticle',
    'specificArticles',
]);
export const TaskRuleConditions = z.object({
    articleRead: z.object({
        scope: ArticleReadTaskScope,
        articleIds: z.array(z.string().uuid()).max(200).optional(),
    }).optional(),
});
export const EngagementTask = z.object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    groupId: z.string().uuid().nullable(),
    title: z.string(),
    description: z.string(),
    status: z.enum(['draft', 'active', 'paused', 'archived']),
    reviewRequired: z.boolean(),
    startsAt: z.string().datetime({ offset: true }).nullable(),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const TaskRule = z.object({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    eventKind: z.string(),
    category: z.string(),
    points: z.number().int().positive(),
    dailyCap: z.number().int().nonnegative().nullable(),
    beneficiary: TaskBeneficiary,
    requireReview: z.boolean(),
    minReviewPoints: z.number().int().nonnegative(),
    conditions: TaskRuleConditions,
    riskConfig: z.record(z.string(), z.unknown()),
    enabled: z.boolean(),
});
export const IntegrityReview = z.object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    groupId: z.string().uuid().nullable(),
    userId: z.string().uuid(),
    userDisplayName: z.string().nullable(),
    status: z.enum(['pending', 'approved', 'rejected', 'reversed']),
    reason: z.string(),
    riskSignals: z.record(z.string(), z.unknown()),
    reviewerNote: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    reviewedAt: z.string().datetime({ offset: true }).nullable(),
});
export const RewardItem = z.object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    groupId: z.string().uuid().nullable(),
    title: z.string(),
    description: z.string(),
    status: z.enum(['draft', 'preview', 'open', 'closed', 'archived']),
    cost: z.number().int().positive(),
    totalStock: z.number().int().nonnegative().nullable(),
    remainingStock: z.number().int().nonnegative().nullable(),
    perUserLimit: z.number().int().positive().nullable(),
    requiresApproval: z.boolean(),
    fulfillmentKind: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const RewardWindow = z.object({
    id: z.string().uuid(),
    rewardItemId: z.string().uuid(),
    previewStartsAt: z.string().datetime({ offset: true }).nullable(),
    opensAt: z.string().datetime({ offset: true }).nullable(),
    closesAt: z.string().datetime({ offset: true }).nullable(),
    redemptionEndsAt: z.string().datetime({ offset: true }).nullable(),
});
export const RewardRedemption = z.object({
    id: z.string().uuid(),
    rewardItemId: z.string().uuid(),
    rewardTitle: z.string().nullable().optional(),
    poolId: z.string().uuid(),
    userId: z.string().uuid(),
    userDisplayName: z.string().nullable().optional(),
    quantity: z.number().int().positive(),
    pointsSpent: z.number().int().positive(),
    status: z.enum(['pending', 'approved', 'fulfilled', 'rejected', 'cancelled']),
    requestedAt: z.string().datetime({ offset: true }),
    decidedAt: z.string().datetime({ offset: true }).nullable(),
    fulfilledAt: z.string().datetime({ offset: true }).nullable(),
});
//# sourceMappingURL=engagement.js.map